/*
 * @Author: lbh
 * @Date: 2025-04-23 14:36:46
 * @LastEditors: lbh
 * @LastEditTime: 2025-04-23 14:37:18
 * @Description: 金額計算
 */
import Decimal from "decimal.js";

const math = {
  // ? 加法运算：计算 a + b 的结果
  // ? 使用方式：this.$Decimal.add(10, 20) => 30
  // ? 参数：a - 被加数（默认值：0），b - 加数（默认值：0）
  // ? 返回：Number 类型的结果
  add(a = 0, b = 0) {
    if(a === undefined || a === null){
      a = 0;
    }
    if(b === undefined || b === null){
      b = 0;
    }
    return new Decimal(a).add(b).toNumber();
  },
  
  // ? 减法运算：计算 a - b 的结果
  // ? 使用方式：this.$Decimal.subtract(30, 10) => 20
  // ? 参数：a - 被减数（默认值：0），b - 减数（默认值：0）
  // ? 返回：Number 类型的结果
  subtract(a = 0, b = 0) {
    // console.log("subtract",a, b);
    // * 增加處理, a, b 不可為空
    if(a === undefined || a === null){
      a = 0;
    }
    if(b === undefined || b === null){
      b = 0;
    }
    return new Decimal(a).sub(b).toNumber();
  },
  
  // ? 乘法运算：计算 a * b 的结果
  // ? 使用方式：this.$Decimal.multiply(10, 3) => 30
  // ? 参数：a - 被乘数（默认值：0），b - 乘数（默认值：0）
  // ? 返回：Number 类型的结果
  multiply(a = 0, b = 0) {
    if(a === undefined || a === null){
      a = 0;
    }
    if(b === undefined || b === null){
      b = 0;
    }
    return new Decimal(a).mul(b).toNumber();
  },
  
  // ? 除法运算：计算 a / b 的结果
  // ? 使用方式：this.$Decimal.divide(30, 3) => 10
  // ? 参数：a - 被除数（默认值：0），b - 除数（默认值：0）
  // ? 返回：Number 类型的结果
  divide(a = 0, b = 0) {
    return new Decimal(a).div(b).toNumber();
  },
  
  // ? 大于比较：判断 a 是否大于 b
  // ? 使用方式：this.$Decimal.greaterThan(10, 5) => true
  // ? 参数：a - 比较值（默认值：0），b - 被比较值（默认值：0）
  // ? 返回：Boolean 类型，true 表示 a > b，false 表示 a <= b
  greaterThan(a = 0, b = 0) {
    return new Decimal(a).greaterThan(b);
  },
  
  // ? 大于等于比较：判断 a 是否大于或等于 b
  // ? 使用方式：this.$Decimal.greaterThanOrEqual(10, 10) => true
  // ? 参数：a - 比较值（默认值：0），b - 被比较值（默认值：0）
  // ? 返回：Boolean 类型，true 表示 a >= b，false 表示 a < b
  greaterThanOrEqual(a = 0, b = 0) {
    return new Decimal(a).greaterThanOrEqualTo(b);
  },
  
  // ? 小于等于比较：判断 a 是否小于或等于 b
  // ? 使用方式：this.$Decimal.lessThanOrEqual(5, 10) => true
  // ? 参数：a - 比较值（默认值：0），b - 被比较值（默认值：0）
  // ? 返回：Boolean 类型，true 表示 a <= b，false 表示 a > b
  lessThanOrEqual(a = 0, b = 0) {
    return new Decimal(a).lessThanOrEqualTo(b);
  },
  
  // ? 数字转特定格式金额：计算金额 * 数量，格式化为指定小数位，并添加单位前缀
  // ? 使用方式：this.$Decimal.formatCurrency(10, 1, 2, '$ ') => '$ 10.00'
  // ? 使用方式：this.$Decimal.formatCurrency(10, 2, 1, '¥ ') => '¥ 20.0'
  // ? 使用方式：this.$Decimal.formatCurrency(10.123, 1) => '$ 10.123' (不指定小数位时保持原小数位)
  // ? 使用方式：this.$Decimal.formatCurrency(10, 1, undefined, '$ ', 2) => '$ 10.00' (没有小数位时补全到默认小数位)
  // ? 参数：amount - 金额（默认值：0），quantity - 数量（默认值：1），decimals - 小数位（默认值：undefined，不指定则保持原小数位），unit - 金额单位（默认值：'$ '），defaultDecimals - 默认小数位（默认值：undefined，当没有小数位时补全）
  // ? 返回：String 类型，格式化后的金额字符串（包含千分位分隔符）
  formatCurrency(amount = 0, quantity = 1, decimals, unit = '$ ', defaultDecimals = 1) {
    if(amount === undefined || amount === null){
      amount = 0;
    }
    if(quantity === undefined || quantity === null){
      quantity = 1;
    }
    if(unit === undefined || unit === null){
      unit = '';
    }
    const total = new Decimal(amount).mul(quantity);
    let formatted;
    // * 如果 decimals 有值，强制使用指定小数位
    if(decimals !== undefined && decimals !== null){
      formatted = total.toFixed(decimals);
    } else {
      // * 如果 decimals 未传入，检查是否有小数位
      const totalStr = total.toString();
      const hasDecimal = totalStr.includes('.');
      
      if(hasDecimal){
        // * 有小数位，保持原有小数位
        formatted = totalStr;
      } else {
        // * 没有小数位，如果指定了 defaultDecimals，则补全
        if(defaultDecimals !== undefined && defaultDecimals !== null){
          formatted = total.toFixed(defaultDecimals);
        } else {
          // * 没有指定 defaultDecimals，保持原样
          formatted = totalStr;
        }
      }
    }
    const formattedWithSeparator = this.formatNumber(formatted);
    return unit + formattedWithSeparator;
  },
  
  // ? 数字转金额格式：将数字格式化为带千分位分隔符的金额格式
  // ? 使用方式：this.$Decimal.formatNumber(10000) => '10,000'
  // ? 使用方式：this.$Decimal.formatNumber('-15021.21') => '-15,021.21'
  // ? 参数：amount - 金额（默认值：0），可以是数字或字符串
  // ? 返回：String 类型，带千分位分隔符的金额字符串
  formatNumber(amount = 0) {
    if(amount === undefined || amount === null){
      amount = 0;
    }
    // * 如果是字符串，直接处理字符串以保留原有小数位
    if(typeof amount === 'string'){
      const parts = amount.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }
    // * 如果是数字，转换为字符串后处理
    const num = new Decimal(amount);
    const parts = num.toFixed().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  },

  // ? 数字进位格式化：根据数值大小自动设置步进值，向上取整后返回带"+"
  // ? 使用方式：this.$Decimal.formatCeil(25, 20) => "20+"
  // ? 使用方式：this.$Decimal.formatCeil(31, 20) => "30+"
  // ? 使用方式：this.$Decimal.formatCeil(105, 20) => "100+"
  // ? 参数：num - 要处理的值，min - 最小阈值（默认20）
  // ? 返回：String 类型，当 num > min 时返回 "XX+"，否则返回原值
  formatCeil(num, min = 20) {
    if (num === undefined || num === null) {
      return min;
    }
    if (num <= min) {
      return num;
    }

    let step = 10;
    if (num >= 100000) step = 10000;
    else if (num >= 10000) step = 1000;
    else if (num >= 1000) step = 100;

    const ceiling = Math.floor(num / step) * step;
    return ceiling + "+";
  },
};

export default {
  install(Vue) {
    Vue.prototype.$Decimal = math;
  },
  ...math,
};
